import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Checkbox } from "antd";
import { ReactComponent as ChevronDown } from "../../assets/Icons/dropdown.svg";
import "./CheckboxFilterDropdown.scss";

const PANEL_WIDTH = 258;
const GAP = 8;
const EDGE = 10;

const CheckboxFilterDropdown = ({
  label = "Products",
  options = [],
  selected = [],
  onChange,
  handleApplyFilter = () => {},
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  /* The dropdown lives inside a PanelCard, which sets overflow:hidden, so an
     absolutely positioned panel gets sliced at the card edge - badly on narrow
     screens. Rendering into <body> with fixed coords avoids every ancestor
     clip; the trade-off is having to place it ourselves. */
  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = r.right - PANEL_WIDTH; // right-aligned to the trigger
    left = Math.min(Math.max(left, EDGE), vw - PANEL_WIDTH - EDGE);

    const below = vh - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const dropUp = below < 220 && above > below;

    setPos({
      left,
      top: dropUp ? undefined : r.bottom + GAP,
      bottom: dropUp ? vh - r.top + GAP : undefined,
      maxHeight: Math.max(180, dropUp ? above : below),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();

    const onDocDown = (e) => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !panelRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  const allValues = useMemo(() => options.map((o) => o.value), [options]);
  const allSelected =
    allValues.length > 0 && allValues.every((v) => selected.includes(v));

  const toggle = (value) => {
    onChange &&
      onChange(
        selected.includes(value)
          ? selected.filter((v) => v !== value)
          : [...selected, value],
      );
  };

  const panel = (
    <div
      ref={panelRef}
      className="cfd__panel"
      role="dialog"
      style={{
        width: PANEL_WIDTH,
        left: pos?.left ?? -9999,
        top: pos?.top,
        bottom: pos?.bottom,
      }}
    >
      <div className="cfd__head">
        <span className="cfd__title">{label}</span>
        <span className="cfd__headright">
          <span className="cfd__tally">
            {selected.length}/{options.length}
          </span>
          {options.length > 1 && (
            <button
              type="button"
              className="cfd__bulk"
              onClick={() => onChange && onChange(allSelected ? [] : allValues)}
            >
              {allSelected ? "Clear" : "Select all"}
            </button>
          )}
        </span>
      </div>

      <div
        className="cfd__list"
        style={{ maxHeight: (pos?.maxHeight ?? 260) - 100 }}
      >
        {options.length === 0 ? (
          <p className="cfd__empty">No options available</p>
        ) : (
          options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`cfd__row${checked ? " is-checked" : ""}`}
              >
                <Checkbox checked={checked} onChange={() => toggle(opt.value)} />
                <span className="cfd__rowlabel">{opt.label}</span>
              </label>
            );
          })
        )}
      </div>

      <div className="cfd__foot">
        <button
          type="button"
          className="cfd__apply"
          onClick={() => {
            handleApplyFilter(selected);
            setOpen(false);
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <div className="cfd">
      <button
        ref={triggerRef}
        type="button"
        className={`cfd__trigger${open ? " is-open" : ""}${
          selected.length ? " has-value" : ""
        }`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="cfd__triggerlabel">{label}</span>
        {selected.length > 0 && (
          <span className="cfd__count">{selected.length}</span>
        )}
        <ChevronDown className="cfd__chevron" />
      </button>

      {open && createPortal(panel, document.body)}
    </div>
  );
};

export default React.memo(CheckboxFilterDropdown);
